import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';

import { SortTableArrow } from '@/lib/components/SortTableArrow';
import { Button, Flex, IconSvg, Text } from '@/lib/v2/components';
import { CalendarIcon, GroupsIcon, ReportIcon, StateIcon } from '@/lib/v2/icons/outline';
import {
  ActionCopyIcon,
  ActionMasterIcon,
  EmailAPIIcon,
  EmailAttachmentIcon,
  EmailIcon,
  EmailTriggerIcon,
  ListIcon,
  ReportIcon as ReportIconSolid,
  SmsAPIIcon,
  SmsIcon,
} from '@/lib/v2/icons/solid';

import { ECampaignsTypeOptions } from '@/src/modules/CampaignsModule/interfaces/Campaigns';

import { atomDetailCampaign } from '@/modules/CampaignsModule/atoms/campaignAtom';
import { TitleHeadCell } from '@/modules/CampaignsModule/components/TitleHeadCell';
import {
  DEFAULT_ORDERBY_ACTIONS,
  DROPDOWN_ACTIONS_STATE,
  MASTER_ACTION,
} from '@/modules/CampaignsModule/constants';
import { useCampaignActionsMutationContext } from '@/modules/CampaignsModule/contexts/CampaignActionsContext';
import {
  ChannelInfo,
  EChannelsID,
  ICampaignActions,
} from '@/modules/CampaignsModule/interfaces/CampaignActions';
import { DateFormat, formatDate } from '@/modules/CampaignsModule/utils';

export const useActionsTableColumns = () => {
  const { t } = useTranslation();
  const detailCampaign = useAtomValue(atomDetailCampaign);
  const { setOrderBy, changeTablePage, setClearFilterBy } = useCampaignActionsMutationContext();
  const [tableOrderByList, setTableOrderByList] = useState(DEFAULT_ORDERBY_ACTIONS);

  const isManualCampaign = useMemo(
    () => detailCampaign?.campaignTypeId === ECampaignsTypeOptions.MANUAL,
    [detailCampaign]
  );

  const currentState = useCallback(
    (status: number) => {
      const [filter] = DROPDOWN_ACTIONS_STATE.filter((item) => Number(item.value) === status);
      return t(`CAMPAIGN_ACTIONS_MAIN.STATES.${filter.label}`) ?? '-';
    },
    [t]
  );

  const channelInfoByID: { [key in EChannelsID]: ChannelInfo } = useMemo(() => {
    return {
      [EChannelsID.EMAIL]: {
        icon: <IconSvg height="auto" svgComponent={<EmailIcon />} width="30px" />,
        route: 'email',
      },
      [EChannelsID.EMAIL_TRIGGER]: {
        icon: <IconSvg height="auto" svgComponent={<EmailTriggerIcon />} width="30px" />,
        route: 'email-trigger',
      },
      [EChannelsID.SMS]: {
        icon: <IconSvg height="auto" svgComponent={<SmsIcon />} width="30px" />,
        route: 'sms',
      },
      [EChannelsID.EMAIL_ATTACHMENT]: {
        icon: <IconSvg height="auto" svgComponent={<EmailAttachmentIcon />} width="30px" />,
        route: 'email-attachment',
      },
      [EChannelsID.EMAIL_API]: {
        icon: <IconSvg height="auto" svgComponent={<EmailAPIIcon />} width="30px" />,
        route: 'mail-api',
      },
      [EChannelsID.SMS_API]: {
        icon: <IconSvg height="auto" svgComponent={<SmsAPIIcon />} width="30px" />,
        route: 'sms-api',
      },
    };
  }, []);

  const iconByChannel = useCallback(
    (channel: number) => {
      const currentChannel = channelInfoByID[channel as keyof typeof channelInfoByID];

      if (isManualCampaign) return currentChannel?.icon ?? null;

      return channel === MASTER_ACTION ? (
        <IconSvg height="auto" svgComponent={<ActionMasterIcon />} width="30px" />
      ) : (
        <div className="pl-3">
          <IconSvg height="auto" svgComponent={<ActionCopyIcon />} width="20px" />
        </div>
      );
    },
    [channelInfoByID, isManualCampaign]
  );

  const changeOrderBy = useCallback(
    (element: { id: string; isAsc: boolean | undefined }) => {
      const orderDirectionValue = element.isAsc === true ? 'desc' : 'asc';
      setOrderBy({ sortField: element.id, sortOrder: orderDirectionValue });
    },
    [setOrderBy]
  );

  /** cspell:disable */
  const nameColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell icon={<ListIcon />} text={t('CAMPAIGN_ACTIONS_MAIN.TABLE.COLUMNS.name')} />
        {isManualCampaign && (
          <SortTableArrow
            elementName={'name'}
            getTableOrderByList={changeOrderBy}
            setTableOrderByList={setTableOrderByList}
            tableOrderByList={tableOrderByList}
          />
        )}
      </Flex>
    ),
    accessor: 'action',
    id: 'action',
    Cell: ({ cell: { value } }: { cell: { value: ICampaignActions } }) => (
      <Flex withGap alignment="start">
        <Flex noGrow withGap alignment="start" className="gap-4" gapSize="small">
          {iconByChannel(value.channel)}
          <Text className="self-start text-emblue-subheadings" fontWeight="medium" variant="text">
            {value.name}
          </Text>
        </Flex>
      </Flex>
    ),
  };

  const statusColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell icon={<StateIcon />} text={t('CAMPAIGN_ACTIONS_MAIN.TABLE.COLUMNS.state')} />
      </Flex>
    ),
    accessor: 'action',
    id: 'status',
    Cell: ({ cell: { value } }: { cell: { value: ICampaignActions } }) => (
      <Flex withGap alignment="start">
        <Flex column noGrow>
          <Text className="self-start text-emblue-subheadings" variant="text">
            {currentState(value.status)}
          </Text>
        </Flex>
      </Flex>
    ),
  };

  const creationDateColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell
          icon={<CalendarIcon />}
          text={t('CAMPAIGN_ACTIONS_MAIN.TABLE.COLUMNS.creation')}
        />
        {isManualCampaign && (
          <SortTableArrow
            elementName={'creationDate'}
            getTableOrderByList={changeOrderBy}
            setTableOrderByList={setTableOrderByList}
            tableOrderByList={tableOrderByList}
          />
        )}
      </Flex>
    ),
    accessor: 'action',
    id: 'creationDate',
    Cell: ({ cell: { value } }: { cell: { value: ICampaignActions } }) => {
      const formattedDate = formatDate(value.creationDate, DateFormat.LongDate);
      return (
        <Flex withGap alignment="start">
          <Flex column noGrow>
            <Text className="self-start text-emblue-subheadings" variant="text">
              {formattedDate}
            </Text>
          </Flex>
        </Flex>
      );
    },
  };

  const durationColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell
          icon={<CalendarIcon />}
          text={t('CAMPAIGN_ACTIONS_MAIN.TABLE.COLUMNS.execution')}
        />
        {isManualCampaign && (
          <SortTableArrow
            elementName={'exeDate'}
            getTableOrderByList={changeOrderBy}
            setTableOrderByList={setTableOrderByList}
            tableOrderByList={tableOrderByList}
          />
        )}
      </Flex>
    ),
    accessor: 'action',
    id: 'exeDate',
    Cell: ({ cell: { value } }: { cell: { value: ICampaignActions } }) => {
      const formattedDate = value.exeDate ? formatDate(value.exeDate, DateFormat.LongDate) : '-';
      return (
        <Flex withGap alignment="start">
          <Flex column noGrow>
            <Text className="self-start text-emblue-subheadings" variant="text">
              {formattedDate}
            </Text>
          </Flex>
        </Flex>
      );
    },
  };

  const audienceColumn = {
    Header: () => (
      <Flex withGap alignment="start">
        <TitleHeadCell
          icon={<GroupsIcon />}
          text={t('CAMPAIGN_ACTIONS_MAIN.TABLE.COLUMNS.audience')}
        />
        {isManualCampaign && (
          <SortTableArrow
            elementName={'audience'}
            getTableOrderByList={changeOrderBy}
            setTableOrderByList={setTableOrderByList}
            tableOrderByList={tableOrderByList}
          />
        )}
      </Flex>
    ),
    accessor: 'action',
    id: 'audience',
    Cell: ({ cell: { value } }: { cell: { value: ICampaignActions } }) => (
      <Flex withGap alignment="start">
        <Flex column noGrow>
          <Text className="self-start text-emblue-subheadings" variant="text">
            {value.audience || '-'}
          </Text>
        </Flex>
      </Flex>
    ),
  };

  /** cspell:enable */

  const actionsColumns: Column<object>[] = [
    nameColumn,
    statusColumn,
    creationDateColumn,
    durationColumn,
    audienceColumn,
  ].filter((x) => x) as Column<object>[];

  return {
    channelInfoByID,
    actionsColumns,
    changeTablePage,
    setClearFilterBy,
  };
};
